import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { useMyNav } from "@/hooks/useMyNav";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const { settings, isLoading: settingsLoading, dismissBanner } = useOrgSettings();
  const location = useLocation();
  const [wizardOpen, setWizardOpen] = useState(true);
  const { nav, loading: navLoading } = useMyNav();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Required onboarding for brand-new managers (no settings row at all).
  // People who only contribute or view reports never see it.
  // Once the wizard is completed OR dismissed, we never block again.
  const needsOnboarding =
    !settingsLoading &&
    settings === null &&
    !navLoading &&
    nav.is_manager &&
    !(nav as { support_org_id?: string | null }).support_org_id && // never in support mode
    !location.pathname.startsWith("/admin");

  if (needsOnboarding) {
    return (
      <>
        {children}
        <OnboardingWizard
          open={wizardOpen}
          onClose={() => {
            // Auto-record dismissal so users who close the wizard aren't
            // re-prompted on every visit. The wizard's own complete/skip
            // paths already write their own stub row, so this is a safe
            // no-op in those cases (settings will already be non-null).
            setWizardOpen(false);
            if (settings === null) {
              try { dismissBanner(); } catch { /* non-blocking */ }
            }
          }}
        />
      </>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
